import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faTimes,
} from '@fortawesome/free-solid-svg-icons';
import PipelineProjectService from '@frontend/AppSettings/services/PipelineProjectService';
import { money, percent, useFinancialVisible } from '@frontend/BidSheets/financialDisplay';
import BidVsActualTrend from './BidVsActualTrend';
import PhaseReadinessStrip from './PhaseReadinessStrip';
import { PipelinePhaseSchedule } from './PipelinePhaseSchedule';
import { buildPhaseGantt, projectCalendarSpan } from './phaseGanttModel';

/**
 * The Project Schedule tab: the demo's layout (KPI row + Bid-vs-Actual popout + CSS phase
 * Gantt) over THIS branch's payloads.
 *
 * The Gantt is phases, not milestones: a phase has a window (planned_start → planned_end)
 * and the demo bars are windows. The production AmCharts Gantt stays on the cross-project
 * Schedule page — a milestone is a point, and substituting it here would flatten every bar
 * to a day.
 *
 * The KPI row (A6) is the demo's six cards minus weather — the recorded skip: weather is
 * per-ticket free text with no impact rollup, and a card must not aggregate what does not
 * exist. Cards 2–3 (budget, margin) are money and render only for financial callers; a blind
 * caller sees exactly the three quantity cards (schedule progress minus its slip sub-line,
 * man-hours, hands on site) — matching the server's route gating.
 *
 * Money discipline, same as every bid screen:
 *  - A 403 on bid-vs-actual renders an EXPLICIT "not permitted" panel — never an empty table,
 *    which reads as "this project has no costs" and is a lie in both directions.
 *  - Figures that DO arrive render through `financialDisplay.money`, which starts redacted and
 *    re-renders when capabilities resolve — so a blind user never sees a figure, not even a $0
 *    flash on first paint.
 */
interface ProjectScheduleTabProps {
  matterid: string;
  /** Kept so the parent handoff stays stable; the Gantt labels phases by their own names. */
  matterName?: string;
}

const num = (v: string | null | undefined): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

// Explicit limit — the backend defaults to 50 and TRUNCATES SILENTLY. Still exported because
// the header's Progress chip shares ['ProjectMilestones'] and an identical queryFn; two
// hand-copied limits is how near-miss keys are born.
export const MILESTONE_LIMIT = 200;

const ProjectScheduleTab = ({ matterid }: ProjectScheduleTabProps): JSX.Element => {
  useFinancialVisible();   // re-render when the money capability resolves

  const [showBva, setShowBva] = useState(false);

  const bva = useQuery({
    queryKey: ['BidVsActual', matterid],
    queryFn: () => PipelineProjectService.getBidVsActual(matterid),
  });
  // The KPI row's quantity sources — every key SHARED verbatim with its existing owner
  // (FieldTimesheetTab's phases, CommercialTab's crew loading and FA summary): a near-miss key
  // doubles the fetch and lets two copies of one figure disagree mid-refetch (plan hazard).
  const phases = useQuery({
    queryKey: ['ProjectPhases', matterid],
    queryFn: () => PipelineProjectService.getPhases(matterid),
  });
  const production = useQuery({
    queryKey: ['ProjectProduction', matterid],
    queryFn: () => PipelineProjectService.getProduction(matterid),
  });
  const crew = useQuery({
    queryKey: ['CrewLoading', matterid],
    queryFn: () => PipelineProjectService.getCrewLoading(matterid),
  });
  const faSummary = useQuery({
    queryKey: ['ForceAccountSummary', matterid],
    queryFn: () => PipelineProjectService.getForceAccountSummary(matterid),
  });

  // Narrowed ONCE (the CommercialTab pnlData pattern): `permitted` discriminates the union,
  // but that narrowing does not survive the repeated `bva.data` reads in the JSX below —
  // tsc rightly re-widens them, and `!` would strip null without excluding the Denied member.
  const bvaData = bva.data?.permitted ? bva.data : null;
  const bvaCost = bvaData?.cost ?? null;
  const faData = faSummary.data?.permitted ? faSummary.data : null;

  // ── Card 1: schedule progress (Day X/Y, calendar days) ────────────────────────────────────
  // No working-day calendar exists, so the span is CALENDAR days — said in the sub-label rather
  // than passed off as working days. Dateless phases → "Unscheduled", never NaN (plan hazard:
  // conversion-day projects clamp to Day 1; finished projects cap at Y).
  const span = projectCalendarSpan(phases.data ?? []);
  const dayX = span?.dayX ?? null;
  const dayY = span?.dayY ?? null;
  const datedPhases = (phases.data ?? []).filter((p) => p.planned_start && p.planned_end);
  // "~Nd projected slip" = max days_pushed from the FA cascade — SEE_FINANCIALS-gated route, so
  // the sub-line exists only for permitted callers; the day count itself degrades gracefully.
  const slipDays = (faData?.cascade?.phases ?? []).reduce((m, p) => Math.max(m, p.days_pushed), 0);
  const pctComplete = bvaData?.percent_complete != null
    ? Math.round(Number(bvaData.percent_complete))
    : null;

  // ── Card 2: budget status ─────────────────────────────────────────────────────────────────
  // Sign convention per the payload: projected_variance < 0 = over (bid_vs_actual_service).
  const projVariance = bvaData ? num(bvaData.totals.projected_variance) : undefined;
  const overBudget = projVariance !== undefined && projVariance < 0;

  // ── Card 4: man-hours to date vs planned ──────────────────────────────────────────────────
  // Actual hours include FA days (hours worked are hours worked — the crew_loading presence
  // doctrine); the planned side is the labour-only baseline the phases-first payload carries.
  const prodRows = production.data ?? [];
  const actualManHours = prodRows.reduce((s, r) => s + (num(r.man_hours) ?? 0), 0);
  const plannedManHours = prodRows.reduce((s, r) => s + (num(r.planned_man_hours) ?? 0), 0);

  // ── Card 5: hands on site vs planned ──────────────────────────────────────────────────────
  // Actual = the LATEST recorded day by design — the card says "as of <date>", not "today".
  // The planned denominator is NOT Σ planned across all phases: sequential phases make that a
  // concurrent-peak fiction and the card would read chronically under-planned (the service's
  // own warning about summing a cumulative figure against a daily plan). Planned = Σ over
  // phases whose window covers as_of_date; with no dated phase covering it, fall back to the
  // phases the crew actually worked that day.
  const crewRows = crew.data?.crews ?? [];
  const asOfDate = crew.data?.as_of_date ?? null;
  const actualHands = crewRows.reduce((s, r) => s + (num(r.actual_headcount) ?? 0), 0);
  const activePhaseIds = new Set(
    asOfDate === null ? [] : datedPhases
      .filter((p) => (p.planned_start as string).slice(0, 10) <= asOfDate
        && asOfDate <= (p.planned_end as string).slice(0, 10))
      .map((p) => p.id),
  );
  const plannedBasisRows = activePhaseIds.size > 0
    ? crewRows.filter((r) => r.phase_id !== null && activePhaseIds.has(r.phase_id))
    : crewRows.filter((r) => (num(r.actual_headcount) ?? 0) > 0);
  const plannedHands = plannedBasisRows.reduce((s, r) => s + (num(r.planned_headcount) ?? 0), 0);

  const gantt = useMemo(
    () => buildPhaseGantt({
      phases: phases.data ?? [],
      production: production.isSuccess ? production.data : undefined,
      crew: crew.data?.crews ?? [],
      cascade: faData?.cascade?.phases ?? [],
      bvaRows: bvaData?.by_cost_code ?? null,
    }),
    [phases.data, production.isSuccess, production.data, crew.data, faData, bvaData],
  );

  useEffect(() => {
    if (!showBva) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowBva(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [showBva]);

  return (
    <>
      <div className="pipeline-kpi-bar">
        <div className="pipeline-kpi-row">
          {phases.data && (
            <div className="pipeline-kpi-card steel">
              <div className="pipeline-kpi-label">Schedule progress</div>
              <div className="pipeline-kpi-value">
                {dayX !== null ? `Day ${dayX}/${dayY}` : 'Unscheduled'}
              </div>
              <div className="pipeline-kpi-sub">
                {dayX === null ? 'no dated phases' : (
                  <>
                    {pctComplete != null && `${pctComplete}% complete`}
                    {pctComplete != null && slipDays > 0 && ' · '}
                    {slipDays > 0 ? `~${slipDays}d projected slip` : (pctComplete == null ? 'calendar days' : '')}
                  </>
                )}
              </div>
            </div>
          )}
          {bvaData && (
            <div className={`pipeline-kpi-card ${overBudget ? 'red' : 'green'}`}>
              <div className="pipeline-kpi-label">
                {overBudget ? 'Projected over budget' : 'Budget status'}
              </div>
              <div className={`pipeline-kpi-value ${overBudget ? 'red' : 'green'}`}>
                {projVariance === undefined ? '—'
                  : overBudget ? `+${money(Math.abs(projVariance))}` : 'On budget'}
              </div>
              <div className="pipeline-kpi-sub">
                {money(num(bvaData.totals.projected))} proj vs {money(num(bvaData.totals.planned))} planned
              </div>
            </div>
          )}
          {bvaData?.margin?.projected_margin_pct != null && (() => {
            const marginPct = num(bvaData.margin.projected_margin_pct);
            const marginLow = marginPct !== undefined && marginPct < 0.12;
            return (
              <div className="pipeline-kpi-card amber">
                <div className="pipeline-kpi-label">Projected margin</div>
                <div className={`pipeline-kpi-value${marginLow ? ' red' : ''}`}>
                  {percent(marginPct)}
                </div>
                <div className="pipeline-kpi-sub">
                  Net of FA {percent(num(bvaData.margin.projected_margin_pct_net_fa))}
                  {' · '}FA billable {money(num(bvaData.margin.fa_billable))}
                </div>
              </div>
            );
          })()}
          {production.data && (
            <div className="pipeline-kpi-card accent">
              <div className="pipeline-kpi-label">Man-hours to date</div>
              <div className="pipeline-kpi-value">
                {actualManHours.toLocaleString('en-US')}h
              </div>
              <div className="pipeline-kpi-sub">
                of {plannedManHours.toLocaleString('en-US')}h planned · incl. FA
              </div>
            </div>
          )}
          {crew.data && (
            <div className="pipeline-kpi-card steel">
              <div className="pipeline-kpi-label">Hands on site</div>
              <div className="pipeline-kpi-value">{actualHands.toLocaleString('en-US')}</div>
              <div className="pipeline-kpi-sub">
                {asOfDate
                  ? `vs ${plannedHands.toLocaleString('en-US')} planned · as of ${asOfDate}`
                  : 'no days recorded yet'}
              </div>
            </div>
          )}
        </div>
        <div className="pipeline-schedule-actions">
          <button
            type="button"
            className="pipeline-bva-btn"
            onClick={() => setShowBva(true)}
          >
            <FontAwesomeIcon icon={faChartLine} /> Bid vs Actual
          </button>
        </div>
      </div>

      {/* Build R: the one sanctioned advisory, imminence-triggered and confined to this strip —
          the Team-tab panels stay silent at rest. Renders nothing (and fetches nothing) unless
          a phase is imminent AND short. */}
      {(phases.data?.length ?? 0) > 0 && (
        <PhaseReadinessStrip matterid={matterid} phases={phases.data ?? []} />
      )}

      {phases.isLoading && <p className="pipeline-muted">Loading schedule…</p>}
      {phases.isError && <p className="pipeline-muted">Could not load the schedule.</p>}
      {!phases.isLoading && !phases.isError && gantt.rows.length === 0 && (
        <p className="pipeline-muted">This project has no scheduled phases.</p>
      )}
      {gantt.rows.length > 0 && <PipelinePhaseSchedule matterid={matterid} model={gantt} />}

      {showBva && (
        <div
          className="pipeline-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Bid vs Actual"
        >
          <div className="pipeline-modal">
            <div className="pipeline-modal-head">
              <h3 className="pipeline-modal-title">
                <FontAwesomeIcon icon={faChartLine} /> Bid vs Actual
              </h3>
              <button
                type="button"
                className="pipeline-modal-close"
                onClick={() => setShowBva(false)}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="pipeline-modal-body">
              <p className="pipeline-panel-subtitle">
                Planned from the frozen baseline; actuals arrive with field tickets (Phase 4).
                {bvaData && bvaData.percent_complete != null && (
                  <> {Number(bvaData.percent_complete).toFixed(0)}% of planned metres produced.</>
                )}
              </p>
              {bva.isLoading && <p className="pipeline-muted">Loading…</p>}
              {bva.isError && <p className="pipeline-muted">Could not load Bid vs Actual.</p>}
              {bva.data?.permitted === false && (
                // The explicit refusal. An empty table here would read as "no costs on this project".
                <p className="pipeline-muted">
                  Bid vs Actual is restricted to financial roles on this project.
                </p>
              )}
              {/* num() yields undefined for absent figures — `?? 0` makes the comparison honest
                  (undefined hours are NOT "> 0"; the disclosure needs a real count). */}
              {bvaCost
                && (num(bvaCost.internal_total) !== 0 || (num(bvaCost.unknown_hours) ?? 0) > 0) && (
                <p className="pipeline-muted">
                  {num(bvaCost.internal_total) !== 0 ? (
                    <>
                      Internal cost {money(num(bvaCost.internal_total))}
                      {bvaCost.estimate_share_pct != null && (
                        <> · {percent(num(bvaCost.estimate_share_pct))} estimated</>
                      )}
                    </>
                  ) : (
                    <>Internal cost unrecorded</>
                  )}
                  {(num(bvaCost.unknown_hours) ?? 0) > 0 && (
                    <> · {bvaCost.unknown_hours} h uncosted</>
                  )}
                </p>
              )}
              {bvaData && <BidVsActualTrend bva={bvaData} />}
              {bvaData && (
                <table className="pipeline-table">
                  <thead>
                    <tr>
                      <th>Cost code</th><th>Planned</th><th>Actual</th><th>Variance</th>
                      <th>Projected</th><th>Proj. variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bvaData.by_cost_code.map((row) => (
                      <tr key={row.code}>
                        <td>{row.code === '(uncoded)' ? row.code : `${row.code} — ${row.name}`}</td>
                        <td>{money(num(row.planned))}</td>
                        <td>{money(num(row.actual))}</td>
                        <td>{money(num(row.variance))}</td>
                        <td>{money(num(row.projected))}</td>
                        <td>{money(num(row.projected_variance))}</td>
                      </tr>
                    ))}
                    <tr className="pipeline-totals-row">
                      <td>Total</td>
                      <td>{money(num(bvaData.totals.planned))}</td>
                      <td>{money(num(bvaData.totals.actual))}</td>
                      <td>{money(num(bvaData.totals.variance))}</td>
                      <td>{money(num(bvaData.totals.projected))}</td>
                      <td>{money(num(bvaData.totals.projected_variance))}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectScheduleTab;
